<template>
  <div
    v-loading="isLoading"
    class="image-uploader"
    :class="{'rounded': rounded}">
    <div class="image"
         :style="{backgroundImage: `url(${base64 || path})`}">
      <i v-if="!path && !base64"/>
    </div>
    <div class="btn-choose-place">
      <button
        v-if="isEditMode"
        type="button"
        class="btn choose"
        @click="openEditorDialog">
        {{label}}
      </button>
    </div>
    <el-dialog
      center
      modal
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :title="L10n('img-editor')"
      :custom-class="`dialog-blue dialog-edit-avatar ${!path && !pathTemp ? 'empty' : ''} ${rounded ? 'rounded': ''}`"
      :visible.sync="isVisible"
      @open="handleOpenEditor"
      @close="handleCloseEditor"
      v-loading="isLoading">
      <vue-avatar
        :width="165"
        :height="165"
        :rotation="settings.rotation"
        :borderRadius="rounded ? 165 : 0"
        :border="0"
        :scale="settings.scale"
        :image="path"
        :color="[255,255,255,.5]"
        ref="vueavatar"
        @vue-avatar-editor:image-ready="handleImageReady"/>
      <div class="slides">
        <div class="slider">
          <div class="title">
            <span>{{L10n('scale')}}</span>
            <span class="value">{{settings.scale}}</span>
          </div>
          <el-slider
            v-model="settings.scale"
            :show-tooltip="false"
            :step=".1"
            :min="1"
            :max="3"/>
        </div>
        <div class="slider">
          <div class="title">
            <span>
              <button
                type="button"
                class="btn btn-rotate"
                @click.stop="imageRotate">
                <i class="fas fa-redo-alt"/>
              </button>
              {{L10n('rotation')}}
            </span>
            <span class="value">{{settings.rotationSlice}}</span>
          </div>
          <el-slider
            v-model="settings.rotationSlice"
            :show-tooltip="false"
            :min="-45"
            :max="45"
            @input="imageRotateSlice"/>
        </div>
      </div>
      <div class="buttons">
        <button
          type="button"
          class="btn btn-border orange"
          :disabled="!path"
          @click.stop="handleDelete">
          {{L10n('img-delete')}}
        </button>
        <div class="main">
          <button
            type="button"
            class="btn btn-border blue"
            @click.stop="openChooseDialog">
            {{L10n('img-change')}}
          </button>
          <button
            type="button"
            class="btn btn-green"
            :disabled="!hasImageLoaded && !base64"
            @click.stop="handleApply">
            {{L10n('img-save')}}
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ServiceMediaUploads } from '@/services/media-uploads'

export default {
  name: 'ImageUploader',
  inheritAttrs: false,
  inject: { elForm: { default: '' }, elFormItem: { default: '' } },
  props: {
    model: { type: String, default: '' },
    url: { type: String, default: '/Upload' },
    label: { type: String, default: '' },
    isEditMode: { type: Boolean, default: true },
    rounded: { type: Boolean, default: false }
  },
  data () {
    return {
      // Main
      isVisible: false,
      isLoading: false,
      // modifiers
      settings: this.settingsModel(),
      // temp editor settings
      tempSettings: this.settingsModel(),
      // image
      path: '',
      pathOrigin: '',
      pathTemp: '',
      base64: '',
      hasImageLoaded: false,
      // TODO: Simple detect if user change image (if user open editor => hasChange)
      hasChange: false
    }
  },
  watch: {
    model: 'parse',
    isLoading (val) {
      this.$emit('isLoading', val)
    }
  },
  created () {
    if (this.elForm) {
      if (this.elForm.executeFirstComponents) {
        this.elForm.executeFirstComponents.push(this)
      } else {
        this.elForm.executeFirstComponents = [this]
      }
    }
  },
  mounted () {
    this.parse(this.model)
  },
  methods: {
    settingsModel () {
      return {
        scale: 1,
        rotation: 0,
        rotationSlice: 0,
        rotationIncrement: 0
      }
    },
    /**
     * @desc Parse and update component by path
     * @param path
     */
    parse (path) {
      this.path = path || ''
      if (this.path !== this.pathTemp) {
        this.pathOrigin = this.path
        this.hasImageLoaded = this.hasChange = false
        this.base64 = ''
        // if first init this always catch - it`s right
        try {
          if (this.path) {
            this.$refs.vueavatar.loadImage(this.path)
          } else {
            this.$refs.vueavatar.resetImage()
          }
        } catch (e) {
          // eslint-disable-next-line
          console.warn(e)
        }
      }
    },
    getNameFromPath (path) {
      if (path.length) {
        let start = path.lastIndexOf('/')
        let end = path.lastIndexOf('.')
        if (end === -1) {
          throw Error('Something wrong')
        } else {
          return path.slice(start + 1, end)
        }
      }
      return ''
    },
    /**
     * @desc Trigger open native file choose dialog
     */
    openChooseDialog () {
      this.hasChange = true
      if (this.$refs.vueavatar && this.$refs.vueavatar.$refs.input) {
        this.$refs.vueavatar.$refs.input.click()
      } else {
        // trick to get image when "vueavatar" be mounted (just in case)
        this.$nextTick(this.openChooseDialog)
      }
    },
    clearVueAvatar () {
      if (this.$refs.vueavatar) {
        this.$refs.vueavatar.resetImage()
      } else {
        this.$nextTick(this.clearVueAvatar)
      }
    },
    /**
     * @desc Open Image Editor dialog
     */
    openEditorDialog () {
      this.hasChange = true
      this.isVisible = true
      if (!this.path && !this.base64) this.openChooseDialog()
    },
    /**
     * @desc Image manipulate methods
     */
    imageRotate () {
      if (++this.settings.rotationIncrement > 3) this.settings.rotationIncrement = 0
      this.imageRotateSlice()
    },
    imageRotateSlice () {
      this.settings.rotation = this.settings.rotationIncrement * 90 + this.settings.rotationSlice
    },
    /**
     * @desc rest methods
     */
    resetControls () {
      this.settings = this.settingsModel()
      this.tempSettings = this.settingsModel()
    },
    /**
     * @desc handles
     */
    handleOpenEditor () {
      if (!this.path) this.clearVueAvatar()
    },
    handleCloseEditor () {
      this.settings = { ...this.tempSettings }
      this.hasImageLoaded = false
    },
    handleImageReady () {
      this.resetControls()
      this.hasImageLoaded = true
    },
    handleApply () {
      this.base64 = this.$refs.vueavatar.getImageScaled().toDataURL()
      this.pathTemp = `${this.url}/${this.folder}/${this.randomString()}.png`
      this.$emit('update:model', this.pathTemp)
      this.tempSettings = { ...this.settings }
      this.isVisible = false
    },
    handleDelete () {
      this.$emit('update:model', '')
      this.path = this.pathTemp = this.base64 = ''
      this.hasImageLoaded = this.isVisible = false
    },
    /**
     * @desc Trigger save image
     * @return {Promise}
     */
    handleSave () {
      return new Promise(resolve => {
        this.requestDelete().then(_ => {
          this.requestSave().then(resolve)
        })
      })
    },
    handleDeleteAll () {
      return Promise.resolve()
    },
    /**
     * @desc Request delete exist file on server.
     * If user some change and save and not new image (this.path !== this.pathTemp)
     * @param fromButton {Boolean} flag if user click on button or trigger save
     * @returns {Promise}
     */
    requestDelete () {
      if (this.pathOrigin && this.hasChange) {
        this.isLoading = true
        return ServiceMediaUploads.removeFile({
          filePath: this.pathOrigin
        })
          .then(_ => {
            this.pathOrigin = ''
            this.isLoading = false
          })
          .catch(error => {
            this.isLoading = false
            this.requestErrorHandler(error, this.L10n('general-error'))
            // eslint-disable-next-line
            console.error('Error delete Image', this.path, error)
          })
      } else {
        return Promise.resolve()
      }
    },
    /**
     * @des Request save new file to server
     * @returns {Promise}
     */
    requestSave () {
      if (this.base64 && this.hasChange) {
        this.isLoading = true
        return ServiceMediaUploads.uploadFile({
          fileName: `${this.randomString()}.png`,
          content: this.base64.split(',')[1]
        })
          .then(response => {
            let uploadedFilePath = response.data.filePath
            this.$emit('update:model', uploadedFilePath)
            this.path = uploadedFilePath
            this.isLoading = false
            this.isVisible = false
          })
          .catch(error => {
            this.isLoading = false
            this.requestErrorHandler(error, this.L10n('general-error'))
            // eslint-disable-next-line
            console.error('Error upload Image', this.path, error)
          })
      } else {
        return Promise.resolve()
      }
    }
  }
}
</script>

<style lang="stylus">
  .image-uploader {
    position relative
    &:after {
      content ''
      display block
      padding-bottom 100%
    }
    .image {
      height 100%
      width 100%
      display block
      position absolute
      background-size cover
      background-repeat no-repeat
      border 1px solid #edf2f3
      background-position 50% 50%
      i {
        background url("../../../../public/img/icons/interface-camera.gif") 50% no-repeat
        background-size 40%
        width 100%
        height 100%
        position absolute
        top 0
        left 0
      }
    }
    &.rounded {
      .image {
        border-radius 50%
      }
    }
    .btn-choose-place {
      position absolute
      bottom 1.3rem
      left .5rem
      right .5rem
      text-align center
      .btn.choose {
        padding .4rem 1.2rem
        text-transform uppercase
        font-size 1.2rem
        background #36587a
        border 1px solid #cfdce7
        white-space nowrap
        &:hover,
        &:focus {
          background #36587a
        }
      }
    }
  }
  .el-dialog.dialog-edit-avatar {
    width 100%
    max-width 42rem
    &.el-dialog{
      .el-dialog__header{
        margin 0
        border none
      }
    }
    .el-dialog__body {
      padding 0
      > div:first-child {
        position relative
        &:before {
          content ''
          border 1px solid rgba(0, 0, 0, .2)
          position absolute
          top 0
          left 50%
          width 16.5rem
          height 16.5rem
          margin 0 0 0 -8.25rem
          pointer-events none
        }
      }
    }
    canvas {
      width 16.5rem
      height 16.5rem
      display block
      margin 1rem auto
    }
    &.rounded {
      border-radius 50%
      .el-dialog__body > div:first-child:before {
        border-radius 50%
      }
    }
    &.empty {
      canvas {
        /*opacity .3*/
      }
    }
    .slides {
      border-top 1px solid #ccc
      padding 1rem 1.5rem
      .slider {
        padding 0 0 1.5rem
        .el-slider__runway {
          margin .4rem 0
        }
        .title {
          display flex
          flex-wrap nowrap
          align-items center
          justify-content space-between
          font-size 1.2rem
          font-weight 500
          text-transform uppercase
          .btn-rotate {
            padding .5rem
            border-radius .3rem
            margin 0 .3rem 0 0
          }
          .value {
            color #000
          }
        }
      }
    }
    .buttons {
      border-top 1px solid #ccc
      display flex
      flex-wrap nowrap
      align-items center
      justify-content space-between
      padding 1.5rem
      .btn {
        font-size 1.2rem
        line-height 2.4rem
        padding 0 1rem
        text-transform uppercase
        border-radius .3rem
      }
      .main {
        display flex
        .btn {
          margin 0 0 0 .5rem
        }
      }
    }
  }
</style>
